import { useEffect, useState } from "react";
import "./App.css";
import transition from "./pics/transition.png";
import transition2 from "./pics/transition2.png";
import { db, logEv } from "./services/firebase";
import { useLocation, useNavigate } from "react-router";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { SaleDb } from "./models/sale.db";
import { SaleItemDb } from "./models/sale-item.db";
import Header from "./components/Header/Header";
import ItemsList from "./components/ItemsList/ItemsList";
import { UserDb } from "./models/user.db";
import { cachedSellerNameKey, serverUrl } from "./constants";
import Modal from "react-modal";
import useAuth from "./hooks/useAuth";
import Footer from "./components/Footer/Footer";
import useWindowSize from "./hooks/useWindowSize";
import { isDefined } from "./utils/objects";
import { DownloadModal } from "./DownloadModal";

function useSale(saleName: string) {
  const [sale, setSale] = useState<SaleDb | null | undefined>();
  const [saleItems, setSaleItems] = useState<SaleItemDb[]>([]);
  const [users, setUsers] = useState<UserDb[]>([]);

  useEffect(() => {
    const salesRef = collection(db, "sales");
    const q = query(salesRef, where("saleName", "==", saleName.toLowerCase()));
    const unsubscribe = onSnapshot(q, (snap) => {
      if (snap.docs.length) {
        const doc = snap.docs[0];
        const sale = doc.data() as SaleDb;
        setSale(sale);
      } else {
        setSale(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [saleName]);

  useEffect(() => {
    if (!sale?.id) {
      return () => {};
    }

    const saleItemsRef = collection(db, "sale_items");
    const q = query(saleItemsRef, where("saleId", "==", sale.id));
    const unsubscribe = onSnapshot(q, (snap) => {
      if (snap.docs.length) {
        const docs = snap.docs;
        const saleItems = docs
          .map((doc) => doc.data() as SaleItemDb)
          .filter((si) => si.private !== true)
          .filter((item) => item.resaleValue != null)
          .filter((item) => (item.size ?? "") !== "");
        saleItems.sort((a, b) => b.cItemCreatedAt - a.cItemCreatedAt);
        setSaleItems(saleItems);
      } else {
        setSaleItems([]);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [sale?.id]);

  useEffect(() => {
    if (!sale?.sellerIds.length) {
      return;
    }
    const getUser = async (id: string) => {
      try {
        const res = await fetch(`${serverUrl}/users/getById`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: id,
          }),
        });
        const json = await res.json();
        if (json && json.status === "OK" && json.user) {
          return json.user as UserDb;
        } else {
          return null;
        }
      } catch (e) {
        return null;
      }
    };
    const getUsers = async (ids: string[]) => {
      const respUsers = await Promise.all(ids.map((id) => getUser(id)));
      const nonNullUsers = respUsers.filter((u) => u) as UserDb[];
      setUsers(nonNullUsers);
    };
    getUsers(sale?.sellerIds);
  }, [sale?.sellerIds]);

  return { sale, saleItems, users };
}

function App() {
  const size = useWindowSize();
  const isMobile = size.width <= 800;
  const { fbUser, indyxUser } = useAuth();
  const [modalIsOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const pathArray = path.split("/");
  const saleName = pathArray[1];
  const sellerName = pathArray[2];

  const { sale, saleItems, users } = useSale(saleName);

  const selectedUser = users.find(
    (u) =>
      sellerName &&
      u.username &&
      u.username.toLowerCase() === sellerName.toLowerCase()
  );

  const selectedUserName = selectedUser?.username?.toLowerCase();

  function openModal() {
    setIsOpen(true);
  }

  function afterOpenModal() {
    // references are now sync'd and can be accessed.
    //subtitle.style.color = '#f00';
  }

  function closeModal() {
    setIsOpen(false);
  }

  const okIndyxUser = !!(fbUser && indyxUser);
  const okIndyxData =
    isDefined(indyxUser?.firstName) &&
    isDefined(indyxUser?.lastName) &&
    isDefined(indyxUser?.socialMediaSource) &&
    isDefined(indyxUser?.goals) &&
    isDefined(indyxUser?.sourceAttributionText);

  useEffect(() => {
    if (okIndyxUser && !okIndyxData) {
      setIsOpen(true);
    }
  }, [okIndyxUser, okIndyxData]);

  useEffect(() => {
    if (selectedUserName) {
      const cachedSellerName = localStorage.getItem(cachedSellerNameKey);
      if (!cachedSellerName) {
        localStorage.setItem(cachedSellerNameKey, selectedUserName);
      }
    }
  }, [selectedUserName]);

  const href = window.location.href;

  useEffect(() => {
    logEv("closetsale_page_view", { href: href });
  }, [href]);

  return (
    <div className="App">
      {sale !== null && sale !== undefined ? (
        <div className="wrapper">
          <Header
            logoUrl={`/icons/logo.png`}
            sale={sale}
            onClick={openModal}
            isMobile={isMobile}
          />
          <div
            style={{
              cursor: "pointer",
              marginTop: 120,
              height: isMobile ? 110 : 173,
              display: "flex",
              flexDirection: "row",
              background: isMobile ? "white" : "rgba(245, 241, 229, 1)",
              overflowX: "scroll",
            }}
          >
            {sale.sellerIds.map((id) => {
              const user = users.find((u) => u.id === id);
              const picUrl = user?.profilePhotoUrl;
              const userName = user?.username;
              const isSelected = selectedUser?.id === id;

              return (
                <div
                  key={id}
                  style={{
                    height: isMobile ? 100 : 160,
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onClick={() => {
                    if (!userName) {
                      return;
                    }
                    if (isSelected) {
                      navigate(`/${saleName}`);
                    } else {
                      navigate(`/${saleName}/${userName}`);
                    }
                  }}
                >
                  <div
                    style={{
                      width: isMobile ? 76 : 120,
                      height: isMobile ? 76 : 120,
                      borderRadius: isMobile ? 76 : 120,
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: isSelected ? "red" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: 6,
                      marginBottom: 6,
                      marginLeft: 6,
                      marginRight: 6,
                    }}
                  >
                    <div
                      style={{
                        width: isMobile ? 64 : 100,
                        height: isMobile ? 64 : 100,
                        borderRadius: isMobile ? 64 : 100,
                        backgroundImage: picUrl ? `url(${picUrl})` : undefined,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }}
                    ></div>
                  </div>
                  <div style={{ fontSize: isMobile ? 10 : 15 }}>{userName}</div>
                </div>
              );
            })}
          </div>
          <ItemsList
            items={saleItems.filter((si) =>
              selectedUser ? si.userId === selectedUser.id : true
            )}
            users={users}
            indyxUser={indyxUser}
            onClick={(saleItem) => {
              const parts = [
                "",
                [saleName, sellerName].filter((s) => s).join("/"),
              ].join("/");

              const search = new URLSearchParams({
                item: saleItem.id,
              }).toString();

              const finalUrl = [parts, search].join("?");

              navigate(finalUrl);
              openModal();
            }}
            onShowModal={openModal}
            emptyString={"There are no items for sale"}
          />
        </div>
      ) : null}
      {sale === null ? (
        <div
          style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "start",
            justifyContent: "center",
            fontSize: 40,
            flexDirection: "column",
            backgroundImage: `url(${transition2})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "100%",
            backgroundPosition: "center",
          }}
        >
          <div
            style={{
              fontSize: 30,
              paddingLeft: 40,
              paddingRight: 40,
              paddingBottom: 20,
              fontFamily: "Spezia",
            }}
          >
            Oops...
          </div>
          <div
            style={{
              fontSize: 16,
              textAlign: "left",
              paddingLeft: 40,
              paddingRight: 40,
              fontFamily: "Spezia",
            }}
          >
            This closet is private and can’t be displayed.
            <br />
            <br />
            If you were invited into this closet, please contact the owner so
            they can update their privacy settings.
            <br />
            <br />
            If you think something else is wrong, send us a note at
            help@myindyx.com.
          </div>
        </div>
      ) : null}
      {sale === undefined ? (
        <div
          style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 40,
            backgroundImage: `url(${transition})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "100%",
            backgroundPosition: "center",
          }}
        >
          LOADING...
        </div>
      ) : null}
      {isMobile ? <Footer user={indyxUser} onDownload={openModal} /> : null}
      <Modal
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={closeModal}
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            padding: "0",
            background: "transparent",
            border: "none",
          },
        }}
        contentLabel="Download Modal"
      >
        <DownloadModal closeModal={closeModal} />
      </Modal>
    </div>
  );
}

export default App;
